(() => {
  const SELECTOR_WIDGET = '[data-offer-bundle-widget]';

  function formatMoney(cents) {
    const safeCents = Math.round(Number(cents) || 0);
    const format =
      (Shopify?.currency && Shopify.currency.active_format) ||
      Shopify?.money_format ||
      Shopify?.currency?.money_format ||
      null;

    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return format ? Shopify.formatMoney(safeCents, format) : Shopify.formatMoney(safeCents);
    }

    const code = Shopify?.currency?.active || Shopify?.currency?.active_code || "USD";
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(safeCents / 100);
    } catch {
      return (safeCents / 100).toFixed(2);
    }
  }

  function ceilToDollar(priceInCents) {
    return Math.ceil(priceInCents / 100) * 100;
  }

  function normalizeItems(variantIds, baseQty) {
    const qty = Number.isFinite(baseQty) && baseQty > 0 ? baseQty : 1;
    const counts = new Map();
    variantIds.forEach((id) => {
      if (!id) return;
      const key = String(id);
      counts.set(key, (counts.get(key) || 0) + qty);
    });
    return [...counts.entries()].map(([id, quantity]) => ({ id: Number(id), quantity }));
  }

  /** POST to Shopify Cart API – handles both single and multi-item adds */
  async function addToCart(items, offerCode) {
    const payload = {
      items: items.map((item) => {
        const entry = { id: item.id, quantity: item.quantity };
        if (offerCode) {
          entry.properties = { __offer: offerCode };
        }
        return entry;
      }),
    };

    const res = await fetch(window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/add.js`
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Could not add to cart');
    }

    return res.json();
  }

  /** Fetch latest cart, update header count, publish theme event, then open minicart */
  function refreshAndOpenCart() {
    fetch(window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart.js`
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const cartCount = document.getElementById('CartCount');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
          cart.item_count ? cartCount.classList.remove('hide') : cartCount.classList.add('hide');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'offer-bundle-widget', cart });
        }

        if (window.SLIDECART_STATE && window.SLIDECART_STATE().settings.enabled) {
          window.SLIDECART_UPDATE();
          window.SLIDECART_OPEN();
        } else {
          const cartPopupWrapper = document.querySelector('[data-cart-popup-wrapper]');
          if (cartPopupWrapper) {
            cartPopupWrapper.classList.remove('cart-popup-wrapper--hidden');

            const popupQuantity = document.querySelector('[data-cart-popup-quantity]');
            if (popupQuantity) popupQuantity.textContent = cart.item_count;

            const popupCartQuantity = document.querySelector('[data-cart-popup-cart-quantity]');
            if (popupCartQuantity) popupCartQuantity.textContent = cart.item_count;
          }
        }
      })
      .catch(() => {});
  }

  function initWidget(widget) {
    const container = widget.closest('[data-custom-atc-container]');
    const variantInput = container?.querySelector('[data-custom-atc-variant-id]') || null;
    const optionRadios = widget.querySelectorAll('input[type="radio"][name^="offer_bundle_option_"]');
    const selectGroups = widget.querySelectorAll('[data-offer-bundle-selects]');
    const pickers = widget.querySelectorAll('[data-offer-bundle-picker]');
    const customAtcBtn = widget.querySelector('[data-offer-bundle-atc]');

    const getActiveOptionIndex = () => {
      const checked = widget.querySelector('input[type="radio"][name^="offer_bundle_option_"]:checked');
      return checked ? checked.value : null;
    };

    const getActiveOptionEl = () => {
      const checked = widget.querySelector('input[type="radio"][name^="offer_bundle_option_"]:checked');
      return checked ? checked.closest('[data-offer-bundle-option]') : null;
    };

    const getOfferCodeForOption = (optionEl) => {
      if (!optionEl) return '';
      const discountPercent = parseFloat(optionEl.getAttribute('data-discount-percent')) || 0;
      const itemsCount = parseInt(optionEl.getAttribute('data-items-count'), 10) || 1;
      if (!(discountPercent > 0) || itemsCount <= 1) return '';

      let payForCount = itemsCount;
      if (itemsCount === 2) payForCount = 1;
      else if (itemsCount === 3) payForCount = 2;
      else if (itemsCount === 4) payForCount = 2;
      else if (itemsCount === 5) payForCount = 3;

      const freeCount = Math.max(0, itemsCount - payForCount);
      return `b${payForCount}g${freeCount}`;
    };

    const showGroupForActiveOption = () => {
      const idx = getActiveOptionIndex();
      selectGroups.forEach((group) => {
        const match = group.getAttribute('data-option-index') === idx;
        group.toggleAttribute('hidden', !match);
      });
    };

    optionRadios.forEach((r) => r.addEventListener('change', showGroupForActiveOption));
    showGroupForActiveOption();

    const getMainVariantId = () => {
      if (variantInput) return variantInput.value || null;
      return container?.getAttribute('data-default-variant-id') || null;
    };

    const closeAllPickerPanels = () => {
      widget.querySelectorAll('[data-offer-bundle-picker-panel]').forEach((p) => p.setAttribute('hidden', ''));
    };

    const updatePickerDisplay = (picker) => {
      const input = picker.querySelector('[data-offer-bundle-variant-select]');
      const buttonText = picker.querySelector('[data-offer-bundle-picker-text]');
      const buttonSwatch = picker.querySelector('[data-offer-bundle-picker-swatch]');
      const panel = picker.querySelector('[data-offer-bundle-picker-panel]');
      if (!input || !panel || !buttonText || !buttonSwatch) return;

      const id = String(input.value || '');
      const item = id
        ? panel.querySelector(`[data-offer-bundle-picker-item][data-variant-id="${CSS.escape(id)}"]`)
        : null;
      const itemText = item?.querySelector('.offer-bundle-widget__picker-item-text')?.textContent?.trim();
      const itemSwatchStyle = item?.querySelector('.offer-bundle-widget__picker-item-swatch')?.getAttribute('style') || '';

      if (itemText) buttonText.textContent = itemText;
      if (itemSwatchStyle) buttonSwatch.setAttribute('style', itemSwatchStyle);
    };

    const initPickers = () => {
      pickers.forEach((picker) => {
        const button = picker.querySelector('[data-offer-bundle-picker-button]');
        const panel = picker.querySelector('[data-offer-bundle-picker-panel]');
        const input = picker.querySelector('[data-offer-bundle-variant-select]');
        if (!button || !panel || !input) return;

        updatePickerDisplay(picker);

        button.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = !panel.hasAttribute('hidden');
          closeAllPickerPanels();
          if (!isOpen) {
            panel.removeAttribute('hidden');

            panel.style.left = '';
            panel.style.right = '';
            panel.style.top = '';
            panel.style.bottom = '';

            const btnRect = button.getBoundingClientRect();
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;
            const margin = 12;
            const desiredMaxW = Math.max(0, viewportW - margin * 2);

            panel.style.maxWidth = `${desiredMaxW}px`;

            const panelRect = panel.getBoundingClientRect();
            const spaceBelow = viewportH - btnRect.bottom;
            const spaceAbove = btnRect.top;
            if (panelRect.height > spaceBelow && spaceAbove > spaceBelow) {
              panel.style.top = 'auto';
              panel.style.bottom = `calc(100% + 6px)`;
            }

            const panelRect2 = panel.getBoundingClientRect();
            if (panelRect2.right > viewportW - margin) {
              const overflow = panelRect2.right - (viewportW - margin);
              panel.style.left = `${-overflow}px`;
              panel.style.right = 'auto';
            }
            if (panelRect2.left < margin) {
              const overflow = margin - panelRect2.left;
              panel.style.left = `${overflow}px`;
              panel.style.right = 'auto';
            }
          }
        });

        panel.addEventListener('click', (e) => {
          const item = e.target.closest('[data-offer-bundle-picker-item]');
          if (!item) return;
          const id = item.getAttribute('data-variant-id');
          if (!id) return;
          input.value = String(id);
          updatePickerDisplay(picker);
          panel.setAttribute('hidden', '');
          updatePricesForVariant(getMainVariantId());
        });
      });

      document.addEventListener('click', (e) => {
        if (!widget.contains(e.target)) closeAllPickerPanels();
      });
    };

    const syncDropdownsToVariant = (variantId) => {
      if (!variantId) return;
      widget.querySelectorAll('[data-offer-bundle-variant-select]').forEach((el) => {
        el.value = String(variantId);
        const picker = el.closest('[data-offer-bundle-picker]');
        if (picker) updatePickerDisplay(picker);
      });
    };

    const getVariantsPrices = () => {
      try {
        const json = widget.getAttribute('data-variants-prices');
        return json ? JSON.parse(json) : {};
      } catch (e) {
        console.error('Failed to parse variants prices:', e);
        return {};
      }
    };

    const getSelectedPricesForOption = (option, variantsPrices, fallbackVariantId) => {
      const optionIndex = option.querySelector('input[type="radio"]')?.value;
      const selectGroup = widget.querySelector(`[data-offer-bundle-selects][data-option-index="${CSS.escape(String(optionIndex))}"]`);

      if (!selectGroup) {
        const fallbackPrice = variantsPrices[fallbackVariantId] || 0;
        return [fallbackPrice];
      }

      const variantSelects = selectGroup.querySelectorAll('[data-offer-bundle-variant-select]');
      if (!variantSelects.length) {
        const fallbackPrice = variantsPrices[fallbackVariantId] || 0;
        return [fallbackPrice];
      }

      const prices = [];
      variantSelects.forEach((select) => {
        const selectedVariantId = select.value;
        const price = variantsPrices[selectedVariantId] || 0;
        prices.push(price);
      });

      return prices.length > 0 ? prices : [variantsPrices[fallbackVariantId] || 0];
    };

    const getTopNPricesSum = (prices, n) => {
      const count = Math.max(0, parseInt(n, 10) || 0);
      if (!prices.length || count <= 0) return 0;
      if (prices.length <= count) return prices.reduce((sum, p) => sum + p, 0);
      const sorted = [...prices].sort((a, b) => b - a);
      return sorted.slice(0, count).reduce((sum, p) => sum + p, 0);
    };

    const updatePricesForVariant = (fallbackVariantId) => {
      const variantsPrices = getVariantsPrices();
      if (Object.keys(variantsPrices).length === 0) return;

      widget.querySelectorAll('[data-offer-bundle-option]').forEach((option) => {
        const discountPercent = parseFloat(option.getAttribute('data-discount-percent')) || 0;
        const itemsCount = parseInt(option.getAttribute('data-items-count'), 10) || 1;

        const selectedPrices = getSelectedPricesForOption(option, variantsPrices, fallbackVariantId);
        const highestPrice = Math.max(...selectedPrices);

        if (!highestPrice) return;

        const sumSelectedPrices = selectedPrices.reduce((sum, p) => sum + p, 0);
        const totalOriginalPrice = sumSelectedPrices > 0 ? sumSelectedPrices : highestPrice * itemsCount;

        let totalDiscountedPrice;
        let showWasPrice = false;

        if (discountPercent > 0) {
          const discountMultiplier = (100 - discountPercent) / 100;
          totalDiscountedPrice = ceilToDollar(totalOriginalPrice * discountMultiplier);
          showWasPrice = true;
        } else {
          totalDiscountedPrice = totalOriginalPrice;
        }

        const pricePerItem = Math.floor(totalDiscountedPrice / itemsCount);

        const priceNowEl = option.querySelector('[data-offer-bundle-price-now]');
        if (priceNowEl) priceNowEl.textContent = formatMoney(totalDiscountedPrice);

        const priceWasEl = option.querySelector('[data-offer-bundle-price-was]');
        if (priceWasEl) {
          priceWasEl.textContent = formatMoney(totalOriginalPrice);
          if (showWasPrice) priceWasEl.removeAttribute('hidden');
          else priceWasEl.setAttribute('hidden', '');
        }

        const pricePerItemEl = option.querySelector('[data-offer-bundle-price-per-item]');
        if (pricePerItemEl) pricePerItemEl.textContent = formatMoney(pricePerItem);
      });
    };

    const getSelectedVariantIds = () => {
      const activeIdx = getActiveOptionIndex();
      const activeGroup = activeIdx
        ? widget.querySelector(`[data-offer-bundle-selects][data-option-index="${CSS.escape(String(activeIdx))}"]`)
        : null;
      if (!activeGroup) return [];
      return [...activeGroup.querySelectorAll('[data-offer-bundle-variant-select]')].map((s) => s.value);
    };

    const setButtonLoading = (loading) => {
      if (!customAtcBtn) return;
      if (!customAtcBtn.dataset.originalText) {
        customAtcBtn.dataset.originalText = customAtcBtn.textContent;
      }
      if (loading) {
        customAtcBtn.setAttribute('aria-disabled', 'true');
        customAtcBtn.textContent = 'ADDING...';
      } else {
        customAtcBtn.removeAttribute('aria-disabled');
        customAtcBtn.textContent = customAtcBtn.dataset.originalText || 'ADD TO CART';
      }
    };

    const showError = (message) => {
      const wrapper = container?.querySelector('.product-form__error-message-wrapper');
      const msgEl = wrapper?.querySelector('.product-form__error-message');
      if (wrapper && msgEl) {
        msgEl.textContent = message;
        wrapper.removeAttribute('hidden');
        setTimeout(() => wrapper.setAttribute('hidden', ''), 5000);
      }
    };

    // Custom ATC button click — uses Cart API directly
    if (customAtcBtn) {
      customAtcBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (customAtcBtn.getAttribute('aria-disabled') === 'true') return;

        const variantIds = getSelectedVariantIds();
        if (!variantIds.length) {
          const fallbackId = getMainVariantId();
          if (fallbackId) variantIds.push(fallbackId);
        }

        if (!variantIds.length) {
          showError('Please select a variant.');
          return;
        }

        const items = normalizeItems(variantIds, 1);
        if (!items.length) {
          showError('Please select a variant.');
          return;
        }

        const offerCode = getOfferCodeForOption(getActiveOptionEl());

        setButtonLoading(true);

        try {
          await addToCart(items, offerCode);
          refreshAndOpenCart();
        } catch (err) {
          showError(err.message || 'Something went wrong. Please try again.');
        } finally {
          setButtonLoading(false);
        }
      });
    }

    // Initialise
    let lastVariantId = getMainVariantId();
    syncDropdownsToVariant(lastVariantId);
    updatePricesForVariant(lastVariantId);
    initPickers();

    // Keep variant in sync when the hidden input changes (theme variant picker still works)
    const pollVariant = () => {
      const current = getMainVariantId();
      if (current && current !== lastVariantId) {
        lastVariantId = current;
        syncDropdownsToVariant(current);
        updatePricesForVariant(current);
      }
    };
    setInterval(pollVariant, 250);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.variant?.id) {
          const variantId = String(event.data.variant.id);
          if (variantInput) variantInput.value = variantId;
          lastVariantId = variantId;
          syncDropdownsToVariant(variantId);
          updatePricesForVariant(variantId);
        }
      });
    }

    document.addEventListener('capyera:cross-sell-variant-change', (event) => {
      const variantId = event?.detail?.variantId ? String(event.detail.variantId) : null;
      if (!variantId || variantId === lastVariantId) return;

      if (variantInput) variantInput.value = variantId;
      lastVariantId = variantId;
      syncDropdownsToVariant(variantId);
      updatePricesForVariant(variantId);
    });
  }

  function boot() {
    document.querySelectorAll(SELECTOR_WIDGET).forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
